import React from 'react';

import { Box, Center, Flex, Select, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { DroxyAccordion } from 'components/Droxy/DroxyAccordion';
import { DroxyAccordionSection } from 'components/Droxy/DroxyAccordionSection';
import { DroxyChatPreviewContainer } from 'components/Droxy/DroxyChatPreviewContainer';
import { InputLabelWithInfo } from 'components/InputLabelWithInfo';
import { SectionNumber } from 'components/SectionNumber';
import { CalendlyButton } from 'components/SmartAssistantComponents/CalendlyButton';
import { useSmartAssistantVisualConfigExtractor } from 'hooks/use-smart-assistant-theme-extractor';
import { t } from 'i18next';
import { ToolConfigurationPanelWrapper } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/ToolConfigurationPanelWrapper';
import { ToolStatusSection } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/ToolStatusSection';
import { useLocation } from 'react-router-dom';
import { DEFAULT_CALENDLY_TOOL_CONFIG_FORM_VALUES } from 'shared/constants/smart-assistant';
import { ChatMessage } from 'shared/interfaces/ChatMessage';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import { CalendlyToolConfig } from 'shared/interfaces/tools/Calendly';
import { ToolConfig, ToolKind, ToolTrigger } from 'shared/interfaces/tools/Tool';
import { getToolConfig } from 'shared/interfaces/tools/utils';
import { formHasFieldErrors } from 'shared/utils/form';
import { requiredInputValidator, validateUrl } from 'shared/utils/form-validators';

enum CalendlyPanelSections {
  GENERAL_SETTINGS = 'general-settings'
}

interface CalendlyToolConfigurationPanelProps {
  onUpdate: (toolConfigs: ToolConfig[]) => void;
  onBack: () => void;
  smartAssistant: SmartAssistant;
}

export function CalendlyToolConfigurationPanel({
  onUpdate,
  onBack,
  smartAssistant
}: CalendlyToolConfigurationPanelProps) {
  const location = useLocation();
  const toolConfigs: ToolConfig[] = location.state?.toolConfigs ?? [];
  const maybeExistingToolConfig = getToolConfig(toolConfigs, ToolKind.CALENDLY);
  const isNewToolConfig = !maybeExistingToolConfig;

  const smartAssistantVisualConfig = useSmartAssistantVisualConfigExtractor({ smartAssistant });

  const calendlyToolConfigForm = useForm<CalendlyToolConfig>({
    initialValues: {
      _id: maybeExistingToolConfig?._id,
      kind: ToolKind.CALENDLY as const,
      toolName: ToolKind.CALENDLY as const,
      enabled: maybeExistingToolConfig?.enabled ?? true,
      trigger: maybeExistingToolConfig?.trigger || DEFAULT_CALENDLY_TOOL_CONFIG_FORM_VALUES.trigger,
      eventUrl: maybeExistingToolConfig?.eventUrl || DEFAULT_CALENDLY_TOOL_CONFIG_FORM_VALUES.eventUrl,
      ctaMessage: maybeExistingToolConfig?.ctaMessage || DEFAULT_CALENDLY_TOOL_CONFIG_FORM_VALUES.ctaMessage
    },
    validate: {
      eventUrl: (value) => requiredInputValidator(value) || validateUrl(value),
      ctaMessage: (value) => requiredInputValidator(value),
      trigger: (value) => requiredInputValidator(value)
    }
  });

  const handleUpdate = () => {
    const validation = calendlyToolConfigForm.validate();
    if (validation.hasErrors) return;
    onUpdate([calendlyToolConfigForm.values]);
  };

  const previewMessages: ChatMessage[] = [
    { text: 'Hello! How can I help you today?', fromUser: false },
    { text: 'I want to schedule a meeting', fromUser: true },
    { text: 'Sure, let me help you with that', fromUser: false }
  ];

  const previewToolConfigs = [calendlyToolConfigForm.values];

  return (
    <ToolConfigurationPanelWrapper
      onSave={handleUpdate}
      onBack={onBack}
      onCancel={isNewToolConfig ? onBack : () => calendlyToolConfigForm.reset()}
      hasChanges={calendlyToolConfigForm.isDirty() || isNewToolConfig}
      px={0}>
      <Box>
        <ToolStatusSection
          enabled={calendlyToolConfigForm.values.enabled}
          onToggle={(enabled) => calendlyToolConfigForm.setFieldValue('enabled', enabled)}
        />
      </Box>
      <Flex direction="row" gap="xl" align="flex-start" w="100%">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <DroxyAccordion defaultOpenSections={[CalendlyPanelSections.GENERAL_SETTINGS]}>
            <DroxyAccordionSection
              title={t('SMART_ASSISTANT.TOOLS.CALENDLY.GENERAL_SETTINGS')}
              section={CalendlyPanelSections.GENERAL_SETTINGS}
              icon={<SectionNumber number={1} />}
              hasError={formHasFieldErrors(calendlyToolConfigForm, 'eventUrl', 'ctaMessage', 'trigger')}>
              <Stack spacing="md">
                <Select
                  label={
                    <InputLabelWithInfo
                      label={t('SMART_ASSISTANT.TOOLS.CALENDLY.TRIGGER.LABEL')}
                      infoContentTranslationKey="SMART_ASSISTANT.TOOLS.CALENDLY.TRIGGER.INFO"
                    />
                  }
                  data={[
                    {
                      value: ToolTrigger.BUTTON,
                      label: t('SMART_ASSISTANT.TOOLS.CALENDLY.TRIGGER.BUTTON')
                    },
                    {
                      value: ToolTrigger.FIRST_SMART_ASSISTANT_MESSAGE,
                      label: t('SMART_ASSISTANT.TOOLS.CALENDLY.TRIGGER.AFTER_FIRST_CHATBOT_MESSAGE')
                    },
                    {
                      value: ToolTrigger.WELCOME,
                      label: t('SMART_ASSISTANT.TOOLS.CALENDLY.TRIGGER.AFTER_WELCOME_MESSAGE')
                    },
                    {
                      value: ToolTrigger.INTENT,
                      label: t('SMART_ASSISTANT.TOOLS.CALENDLY.TRIGGER.ON_INTENT')
                    }
                  ]}
                  {...calendlyToolConfigForm.getInputProps('trigger')}
                />
                <TextInput
                  label={
                    <InputLabelWithInfo
                      label={t('SMART_ASSISTANT.TOOLS.CALENDLY.EVENT_URL.LABEL')}
                      infoContentTranslationKey="SMART_ASSISTANT.TOOLS.CALENDLY.EVENT_URL.INFO"
                    />
                  }
                  placeholder={t('SMART_ASSISTANT.TOOLS.CALENDLY.EVENT_URL.PLACEHOLDER')}
                  maxLength={100}
                  {...calendlyToolConfigForm.getInputProps('eventUrl')}
                />
                <TextInput
                  label={
                    <InputLabelWithInfo
                      label={t('SMART_ASSISTANT.TOOLS.CALENDLY.CTA_MESSAGE.LABEL')}
                      infoContentTranslationKey="SMART_ASSISTANT.TOOLS.CALENDLY.CTA_MESSAGE.INFO"
                    />
                  }
                  maxLength={50}
                  {...calendlyToolConfigForm.getInputProps('ctaMessage')}
                />
              </Stack>
            </DroxyAccordionSection>
          </DroxyAccordion>
        </Box>
        <Center w="35%">
          <DroxyChatPreviewContainer
            messages={previewMessages}
            toolConfigs={previewToolConfigs}
            smartAssistantVisualConfig={smartAssistantVisualConfig}
            visibilityOverrides={{
              [ToolKind.CALENDLY]: true
            }}
            inputTopSection={
              calendlyToolConfigForm.values.trigger === ToolTrigger.BUTTON ? (
                <Flex direction="row" align="center">
                  <CalendlyButton
                    toolConfigs={previewToolConfigs}
                    smartAssistantVisualConfig={smartAssistantVisualConfig}
                  />
                </Flex>
              ) : undefined
            }
          />
        </Center>
      </Flex>
    </ToolConfigurationPanelWrapper>
  );
}
